import React, { useState, useRef, useCallback, useEffect, lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiSmile, FiPaperclip, FiCamera, FiSend, FiX } from "react-icons/fi";
import {
  addOptimisticMessage,
  sendMessageThunk,
  editMessageThunk,
  clearReplyingTo,
  clearEditingMessage,
} from "../../../Slices/chatSlice";
import ReplyPreview from "./ReplyPreview";
import ForwardDialog from "./ForwardDialog";
import AISuggestButton from "./AISuggestButton";
import VoiceRecorder from "./VoiceRecorder";
import BotQuickActions from "./BotQuickActions";
import ScreenshotSelector from "./ScreenshotSelector";
import { SERVER_URL } from "../../../databucket";
import socket from "../Sales1/socket";
import { getInitials, getAvatarColor } from "./avatarUtils";

const MediaPicker = lazy(() => import("./MediaPicker"));

const MessageInput = ({ conversationId, isBotConversation }) => {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed, string = searching
  const [mentionIdx, setMentionIdx] = useState(0);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(null);
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const replyingTo = useSelector((state) => state.chat.replyingTo);
  const editingMessage = useSelector((state) => state.chat.editingMessage);
  const allUsers = useSelector((state) => state?.loginUserInfo?.allUsers || []);
  const conversation = useSelector((state) =>
    state.chat.conversations.find((c) => c.id === conversationId)
  );
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const emitTyping = useCallback(() => {
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit("chat:typing", {
        conversationId,
        userId: loginUser?.id,
        userName: loginUser?.firstname
          ? `${loginUser.firstname} ${loginUser.lastname || ""}`.trim()
          : "User",
      });
    }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit("chat:stop-typing", {
        conversationId,
        userId: loginUser?.id,
      });
    }, 2000);
  }, [conversationId, loginUser]);

  const stopTyping = useCallback(() => {
    clearTimeout(typingTimeoutRef.current);
    if (isTypingRef.current) {
      isTypingRef.current = false;
      socket.emit("chat:stop-typing", {
        conversationId,
        userId: loginUser?.id,
      });
    }
  }, [conversationId, loginUser]);

  const uploadFiles = async (fileList) => {
    const formData = new FormData();
    fileList.forEach((f) => formData.append("files", f));
    const res = await fetch(`${SERVER_URL}/upload`, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    return data.files || [];
  };

  const sendSingleMessage = (type, content, fileUrl, fileName, fileMimeType, replyToId) => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const userName = loginUser?.firstname
      ? `${loginUser.firstname} ${loginUser.lastname || ""}`.trim()
      : "User";

    dispatch(
      addOptimisticMessage({
        tempId,
        conversationId,
        senderId: loginUser?.id,
        senderName: userName,
        type,
        content: content || "",
        fileUrl,
        fileName,
        fileMimeType,
        replyTo: replyToId || null,
        createdAt: new Date().toISOString(),
        readBy: [{ userId: loginUser?.id, readAt: new Date().toISOString() }],
      })
    );

    dispatch(
      sendMessageThunk({
        conversationId,
        senderId: loginUser?.id,
        senderName: userName,
        type,
        content: content || "",
        fileUrl,
        fileName,
        fileMimeType,
        replyTo: replyToId || null,
        tempId,
      })
    );
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) return;
    if (!conversationId) return;

    // If editing a message, dispatch edit instead of send
    if (editingMessage && trimmed) {
      dispatch(editMessageThunk({
        messageId: editingMessage.messageId,
        conversationId,
        content: trimmed,
        userId: loginUser?.id,
      }));
      dispatch(clearEditingMessage());
      setText("");
      stopTyping();
      return;
    }

    // Upload files first if any
    if (files.length > 0) {
      setUploading(true);
      try {
        const uploaded = await uploadFiles(files);
        uploaded.forEach((f, idx) => {
          const isImage = f.mimeType?.startsWith("image/");
          // Attach text content to the first file message
          const msgContent = idx === 0 ? trimmed : "";
          const replyId = idx === 0 && replyingTo ? replyingTo.messageId : undefined;
          sendSingleMessage(
            isImage ? "image" : "file",
            msgContent,
            f.url,
            f.originalName,
            f.mimeType,
            replyId
          );
        });
        if (replyingTo) dispatch(clearReplyingTo());
      } catch (err) {
        console.error("Upload failed:", err);
      }
      setUploading(false);
      setFiles([]);
    } else if (trimmed) {
      // Send text-only message (no files)
      sendSingleMessage("text", trimmed, undefined, undefined, undefined, replyingTo?.messageId);
      if (replyingTo) dispatch(clearReplyingTo());
    }

    setText("");
    stopTyping();
    setShowEmoji(false); // collapse the emoji picker after sending — matches mobile UX
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) {
      setFiles((prev) => [...prev, ...selected]);
    }
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEmojiSelect = (emoji) => {
    const ta = textareaRef.current;
    if (ta) {
      const start = ta.selectionStart || text.length;
      const newText = text.slice(0, start) + emoji + text.slice(start);
      setText(newText);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + emoji.length;
      }, 0);
    } else {
      setText((prev) => prev + emoji);
    }
  };

  const handleGifSelect = (gifUrl, title) => {
    if (!conversationId) return;
    sendSingleMessage("gif", gifUrl, gifUrl, title || "GIF", "image/gif");
  };

  const handleStickerSelect = (stickerEmoji, label) => {
    if (!conversationId) return;
    sendSingleMessage("sticker", stickerEmoji, undefined, label);
  };

  const handleScreenshot = () => {
    // Instantly show the selection overlay — no pre-capture delay
    setScreenshotDataUrl("active");
  };

  const handleScreenshotCapture = (file) => {
    setFiles((prev) => [...prev, file]);
    setScreenshotDataUrl(null);
  };

  const handleScreenshotCancel = () => {
    setScreenshotDataUrl(null);
  };

  const handleTextareaChange = (e) => {
    setText(e.target.value);
    emitTyping();
    // Auto-resize
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  };

  // Pre-fill text when editing
  useEffect(() => {
    if (editingMessage) {
      setText(editingMessage.content || "");
      textareaRef.current?.focus();
    }
  }, [editingMessage]);

  // Focus textarea when replying
  useEffect(() => {
    if (replyingTo) {
      textareaRef.current?.focus();
    }
  }, [replyingTo]);

  // Listen for AI text insertions
  useEffect(() => {
    const handler = (e) => {
      const aiText = e.detail?.text;
      if (aiText) {
        setText(aiText);
        textareaRef.current?.focus();
      }
    };
    window.addEventListener("chat:ai-insert", handler);
    return () => window.removeEventListener("chat:ai-insert", handler);
  }, []);

  // @mention autocomplete logic
  const participantIds = conversation?.participants?.map((p) => p.userId) || [];
  const mentionCandidates = allUsers
    .filter((u) => participantIds.includes(u.id) && u.id !== loginUser?.id)
    .filter((u) => {
      if (mentionQuery === null || mentionQuery === "") return true;
      const name = `${u.firstname || ""} ${u.lastname || ""}`.toLowerCase();
      return name.includes(mentionQuery.toLowerCase());
    })
    .slice(0, 6);

  const handleTextareaChangeWithMention = (e) => {
    const val = e.target.value;
    setText(val);
    emitTyping();

    // Detect @mention
    const cursorPos = e.target.selectionStart;
    const beforeCursor = val.slice(0, cursorPos);
    const atMatch = beforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionIdx(0);
    } else {
      setMentionQuery(null);
    }

    // Auto-resize
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  };

  const insertMention = (user) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursorPos = ta.selectionStart;
    const beforeCursor = text.slice(0, cursorPos);
    const afterCursor = text.slice(cursorPos);
    const atIdx = beforeCursor.lastIndexOf("@");
    const name = `${user.firstname || ""} ${user.lastname || ""}`.trim();
    const newText = beforeCursor.slice(0, atIdx) + `@${name} ` + afterCursor;
    setText(newText);
    setMentionQuery(null);
    setTimeout(() => {
      ta.focus();
      const newPos = atIdx + name.length + 2;
      ta.selectionStart = ta.selectionEnd = newPos;
    }, 0);
  };

  const handleMentionKeyDown = (e) => {
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIdx((prev) => Math.min(prev + 1, mentionCandidates.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIdx((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionCandidates[mentionIdx]);
        return;
      }
      if (e.key === "Escape") {
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Voice message handler
  const handleVoiceSend = async (file) => {
    if (!conversationId) return;
    setUploading(true);
    try {
      const uploaded = await uploadFiles([file]);
      if (uploaded.length > 0) {
        const f = uploaded[0];
        sendSingleMessage("file", "", f.url, f.originalName || "Voice message", f.mimeType || "audio/webm");
      }
    } catch (err) {
      console.error("Voice upload failed:", err);
    }
    setUploading(false);
  };

  return (
    <div className="chat-input" style={{ position: "relative" }}>
      {/* Forward Dialog */}
      <ForwardDialog />

      {/* Media Picker (Emoji / GIFs / Stickers) */}
      {showEmoji && (
        <Suspense fallback={null}>
          <MediaPicker
            open={showEmoji}
            onClose={() => setShowEmoji(false)}
            onEmojiSelect={handleEmojiSelect}
            onGifSelect={handleGifSelect}
            onStickerSelect={handleStickerSelect}
          />
        </Suspense>
      )}

      {/* File preview */}
      {files.length > 0 && (
        <div className="chat-input__preview">
          {files.map((file, idx) => (
            <div key={idx} className="chat-input__preview-item">
              {file.type?.startsWith("image/") ? (
                <img
                  className="chat-input__preview-img"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                />
              ) : (
                <div className="chat-input__preview-file">
                  <FiPaperclip />
                </div>
              )}
              <div className="chat-input__preview-name">{file.name}</div>
              <button
                className="chat-input__preview-remove"
                onClick={() => removeFile(idx)}
              >
                <FiX />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="chat-input__uploading">
          <span>Uploading...</span>
          <div className="chat-input__uploading-bar">
            <div className="chat-input__uploading-fill" />
          </div>
        </div>
      )}

      {/* Bot Quick Actions */}
      {isBotConversation && (
        <BotQuickActions onSelect={(cmd) => {
          setText(cmd);
          textareaRef.current?.focus();
        }} />
      )}

      {/* Reply/Edit Preview */}
      <ReplyPreview />

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f1f5f9", borderBottom: "1px solid #cbd5e1" }}>
        <button
          onClick={() => setShowEmoji(!showEmoji)}
          title="Emoji"
          type="button"
          style={{
            padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: showEmoji ? "#eef2ff" : "#fff",
            color: showEmoji ? "#6366f1" : "#64748b",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 13, fontWeight: 500,
          }}
        >
          <FiSmile size={16} />
          <span>Emoji</span>
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          title="Attach file"
          type="button"
          style={{
            padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: "#fff", color: "#64748b",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 13, fontWeight: 500,
          }}
        >
          <FiPaperclip size={16} />
          <span>Attach</span>
        </button>
        <button
          onClick={handleScreenshot}
          title="Screenshot"
          type="button"
          style={{
            padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: "#fff", color: "#64748b",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 13, fontWeight: 500,
          }}
        >
          <FiCamera size={16} />
          <span>Screenshot</span>
        </button>
      </div>

      {/* @Mention autocomplete */}
      {mentionQuery !== null && mentionCandidates.length > 0 && (
        <div className="mention-autocomplete">
          {mentionCandidates.map((user, i) => (
            <button
              key={user.id}
              className={`mention-autocomplete__item ${i === mentionIdx ? "mention-autocomplete__item--active" : ""}`}
              onClick={() => insertMention(user)}
              type="button"
            >
              <div
                className="chat-avatar chat-avatar--sm"
                style={{ background: getAvatarColor(user.id), width: 28, height: 28, fontSize: 10 }}
              >
                {getInitials(user.firstname, user.lastname)}
              </div>
              <div>
                <div className="mention-autocomplete__name">
                  {user.firstname} {user.lastname || ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Text input + send */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "6px 12px 10px" }}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextareaChangeWithMention}
          onKeyDown={handleMentionKeyDown}
          placeholder="Type a message... (use @ to mention)"
          rows={1}
          style={{
            flex: 1, border: "1px solid #e2e8f0", borderRadius: 20,
            padding: "8px 16px", fontSize: "13.5px", color: "#1e293b",
            background: "#f8fafc", resize: "none", outline: "none",
            maxHeight: 120, minHeight: 38, lineHeight: 1.4,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        />
        <VoiceRecorder onSend={handleVoiceSend} />
        <AISuggestButton
          conversationId={conversationId}
          onSelect={(suggestedText) => {
            setText(suggestedText);
            textareaRef.current?.focus();
          }}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() && files.length === 0}
          title="Send"
          type="button"
          style={{
            width: 38, height: 38, borderRadius: "50%", border: "none", padding: 0,
            background: (!text.trim() && files.length === 0) ? "#e2e8f0" : "#6366f1",
            color: (!text.trim() && files.length === 0) ? "#94a3b8" : "#fff",
            cursor: (!text.trim() && files.length === 0) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FiSend size={16} />
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {/* Screenshot selector overlay (portal to body) */}
      {screenshotDataUrl &&
        ReactDOM.createPortal(
          <ScreenshotSelector
            onCapture={handleScreenshotCapture}
            onCancel={handleScreenshotCancel}
          />,
          document.body
        )}
    </div>
  );
};

export default MessageInput;
